import React, { useState } from "react";
import {
	Box,
	Typography,
	Breadcrumbs,
	Link,
	Alert,
	Snackbar,
	CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useCompanyGroup, useUpdateCompanyGroup } from "../../hooks/useCompanyGroup";
import CompanyGroupForm from "../../components/companyGroup/CompanyGroupForm";

const CompanyGroupEdit: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const groupId = parseInt(id || "0");

	const { t } = useTranslation();
	const navigate = useNavigate();

	const { data: companyGroup, isLoading, error } = useCompanyGroup(groupId);
	const updateMutation = useUpdateCompanyGroup();

	const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");
	const [alertSeverity, setAlertSeverity] = useState<"success" | "error">("success");

	const handleUpdate = async (data: { name: string; website?: string; companyIds: number[] }) => {
		try {
			await updateMutation.mutateAsync({
				id: groupId,
				data: {
					name: data.name,
					website: data.website,
					companyIds: data.companyIds,
				},
			});

			setAlertSeverity("success");
			setAlertMessage(t("companyGroup.updatedSuccessfully"));
			setAlertOpen(true);

			setTimeout(() => {
				navigate(`/company-groups/${groupId}`);
			}, 1500);
		} catch (error: any) {
			setAlertSeverity("error");
			const msg = error?.message || "";
			if (msg.includes("already exists")) {
				setAlertMessage(t("companyGroup.nameAlreadyExists", { name: data.name }));
			} else if (msg.includes("already belongs to another group")) {
				setAlertMessage(t("companyGroup.companyAlreadyInOtherGroup"));
			} else {
				setAlertMessage(t("companyGroup.updateError"));
			}
			setAlertOpen(true);
		}
	};

	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" height="400px">
				<CircularProgress />
			</Box>
		);
	}

	if (error || !companyGroup) {
		return (
			<Box p={3}>
				<Typography color="error">
					{t("companyGroup.errorLoading")}: {error?.message || t("companyGroup.notFound")}
				</Typography>
			</Box>
		);
	}

	return (
		<Box p={3}>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
				<Typography variant="h2">{t("companyGroup.editTitle")}</Typography>
			</Box>

			<Breadcrumbs separator=">" aria-label="breadcrumb" sx={{ mb: 3 }}>
				<Link
					underline="hover"
					color="inherit"
					onClick={() => navigate("/company-groups")}
					sx={{ cursor: "pointer" }}
				>
					{t("companyGroup.list")}
				</Link>
				<Link
					underline="hover"
					color="inherit"
					onClick={() => navigate(`/company-groups/${groupId}`)}
					sx={{ cursor: "pointer" }}
				>
					{companyGroup.name}
				</Link>
				<Typography color="text.primary">{t("common.edit")}</Typography>
			</Breadcrumbs>

			<CompanyGroupForm
				initialData={{
					name: companyGroup.name,
					website: companyGroup.website,
				}}
				initialCompanies={companyGroup.companies}
				currentGroupId={groupId}
				onSubmit={handleUpdate}
				isSubmitting={updateMutation.isPending}
				submitButtonText={t("common.save")}
			/>

			<Snackbar
				open={alertOpen}
				autoHideDuration={5000}
				onClose={() => setAlertOpen(false)}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={() => setAlertOpen(false)}
					severity={alertSeverity}
					variant="filled"
					sx={{ width: "100%" }}
				>
					{alertMessage}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default CompanyGroupEdit;
